import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { color, FormGroup, Input, RadioButton } from '@uniquegood/realworld-studio-design';
import { Controller, useWatch } from 'react-hook-form';
import Flatpickr from 'react-flatpickr';
import { Korean } from 'flatpickr/dist/l10n/ko';
import 'flatpickr/dist/flatpickr.css';
import { onMobile } from '@/styles/responsive';
import { ContactFormProps } from '../types';
import { BUSINESS_TYPES, CALL_TIME_OPTIONS, PAYMENT_METHODS } from '../constants';

const inputStyle = css`
  flex: 1;
  min-width: 0;
  width: 100%;
`;

const formGroupStyle = css`
  align-items: center;

  & > div:first-of-type {
    min-width: 110px;
  }

  ${onMobile} {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
`;

const ContactForm = ({ control, errors }: ContactFormProps) => {
  const operationStartDate = useWatch({ control, name: 'operationStartDate' });
  const operationEndDate = useWatch({ control, name: 'operationEndDate' });

  return (
    <FormCard>
      <FormGroup
        label="기업/단체명"
        isHorizontal
        errorText={errors?.organizationName?.message}
        cssStyle={formGroupStyle}
      >
        <Controller
          name="organizationName"
          control={control}
          rules={{
            required: '기업/단체명을 입력해주세요',
            maxLength: {
              value: 30,
              message: '최대 30자까지 입력 가능합니다'
            }
          }}
          render={({ field }) => (
            <Input
              type="text"
              placeholder="기업 또는 단체명을 입력해 주세요"
              maxLength={30}
              value={field.value}
              onChange={(value) => field.onChange(value)}
              cssStyle={inputStyle}
            />
          )}
        />
      </FormGroup>

      <FormGroup
        label="담당자 성함"
        isHorizontal
        errorText={errors?.contactName?.message}
        cssStyle={formGroupStyle}
      >
        <Controller
          name="contactName"
          control={control}
          rules={{
            required: '담당자 성함을 입력해주세요',
            maxLength: {
              value: 30,
              message: '최대 30자까지 입력 가능합니다'
            }
          }}
          render={({ field }) => (
            <Input
              type="text"
              placeholder="성함을 입력해 주세요"
              maxLength={30}
              value={field.value}
              onChange={(value) => field.onChange(value)}
              cssStyle={inputStyle}
            />
          )}
        />
      </FormGroup>

      <FormGroup
        label="부서 및 직책"
        isHorizontal
        errorText={errors?.jobTitle?.message}
        cssStyle={formGroupStyle}
      >
        <Controller
          name="jobTitle"
          control={control}
          rules={{
            required: '부서 및 직책을 입력해주세요',
            maxLength: {
              value: 30,
              message: '최대 30자까지 입력 가능합니다'
            }
          }}
          render={({ field }) => (
            <Input
              type="text"
              placeholder="담당자님이 속한 부서 또는 담당자님의 직책을 입력해 주세요"
              maxLength={30}
              value={field.value}
              onChange={(value) => field.onChange(value)}
              cssStyle={inputStyle}
            />
          )}
        />
      </FormGroup>

      <FormGroup
        label="담당자 연락처"
        isHorizontal
        errorText={errors?.phone?.message}
        cssStyle={formGroupStyle}
      >
        <Controller
          name="phone"
          control={control}
          rules={{
            required: '담당자 연락처를 입력해주세요',
            maxLength: {
              value: 13,
              message: '최대 13자까지 입력 가능합니다'
            },
            pattern: {
              value: /^(01[016789]|02|0[3-6][1-5]|070)-\d{3,4}-\d{4}$/,
              message: '올바른 휴대폰 번호를 입력해주세요 (예: 010-1234-5678)'
            }
          }}
          render={({ field }) => (
            <Input
              type="tel"
              placeholder="전화번호를 입력해 주세요"
              maxLength={13}
              value={field.value}
              onChange={(value) => field.onChange(value)}
              cssStyle={inputStyle}
            />
          )}
        />
      </FormGroup>

      <EmailFormGroupWrapper>
        <FormGroup
          label="업무용 이메일"
          isHorizontal
          errorText={errors?.email?.message}
          cssStyle={formGroupStyle}
        >
          <Controller
            name="email"
            control={control}
            rules={{
              required: '업무용 이메일을 입력해주세요',
              maxLength: {
                value: 100,
                message: '최대 100자까지 입력 가능합니다'
              },
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: '유효한 이메일 주소를 입력해주세요'
              }
            }}
            render={({ field }) => (
              <Input
                type="email"
                placeholder="업무용 이메일을 입력해 주세요"
                maxLength={100}
                value={field.value}
                onChange={(value) => field.onChange(value)}
                cssStyle={inputStyle}
              />
            )}
          />
        </FormGroup>
        <HelpText>남겨주신 문의는 이메일로 최대한 빠르게 연락드려요.</HelpText>
      </EmailFormGroupWrapper>

      <FormGroup label="비즈니스 유형" isHorizontal cssStyle={formGroupStyle}>
        <Controller
          name="businessType"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Select value={field.value} onChange={field.onChange}>
              <option value="" disabled>
                비즈니스 유형을 선택해 주세요
              </option>
              {BUSINESS_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Select>
          )}
        />
      </FormGroup>

      <FormGroup
        id="contentPurpose"
        label="콘텐츠 제작 목적"
        isHorizontal
        errorText={errors?.contentPurpose?.message}
        cssStyle={formGroupStyle}
      >
        <Controller
          name="contentPurpose"
          control={control}
          rules={{ maxLength: { value: 100, message: '최대 100자까지 입력 가능합니다' } }}
          render={({ field }) => (
            <Input
              id="contentPurpose"
              name={field.name}
              placeholder="콘텐츠 제작 목적을 입력해 주세요"
              maxLength={100}
              value={field.value}
              onChange={(value) => field.onChange(value)}
              cssStyle={inputStyle}
            />
          )}
        />
      </FormGroup>

      <FormGroup label="콘텐츠 운영 기간" isHorizontal cssStyle={formGroupStyle}>
        <DateRange>
          <Controller
            name="operationStartDate"
            control={control}
            render={({ field }) => (
              <DateInput
                name={field.name}
                aria-label="콘텐츠 운영 시작일"
                placeholder="시작일"
                value={field.value}
                options={{
                  dateFormat: 'Y-m-d',
                  locale: Korean,
                  disableMobile: true,
                  allowInput: true,
                  maxDate: operationEndDate || undefined
                }}
                onChange={(_, dateString) => field.onChange(dateString)}
              />
            )}
          />
          <span>~</span>
          <Controller
            name="operationEndDate"
            control={control}
            render={({ field }) => (
              <DateInput
                name={field.name}
                aria-label="콘텐츠 운영 종료일"
                placeholder="종료일"
                value={field.value}
                options={{
                  dateFormat: 'Y-m-d',
                  locale: Korean,
                  disableMobile: true,
                  allowInput: true,
                  minDate: operationStartDate || undefined
                }}
                onChange={(_, dateString) => field.onChange(dateString)}
              />
            )}
          />
        </DateRange>
      </FormGroup>

      <FormGroup
        label="사용 기간"
        isHorizontal
        errorText={errors?.useMonths?.message}
        cssStyle={formGroupStyle}
      >
        <Controller
          name="useMonths"
          control={control}
          rules={{
            required: '사용 기간을 입력해주세요',
            validate: (value) => {
              const numValue = Number(value);

              if (Number.isNaN(numValue) || numValue < 1) {
                return '최소 1개월 이상 입력해주세요';
              }

              if (numValue > 60) {
                return '최대 60개월 이하 입력해주세요';
              }

              return true;
            }
          }}
          render={({ field }) => (
            <DurationWrapper>
              <DurationInput
                type="number"
                placeholder="12"
                value={field.value || ''}
                onChange={({ target: { value } }) => {
                  const numValue = value === '' ? undefined : Number(value);
                  field.onChange(numValue);
                }}
              />
              <DurationUnit>개월</DurationUnit>
            </DurationWrapper>
          )}
        />
      </FormGroup>

      <FormGroup label="원하는 결제 방식" isHorizontal cssStyle={formGroupStyle}>
        <Controller
          name="paymentMethod"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <RadioGroup>
              {PAYMENT_METHODS.map((method) => (
                <RadioButton
                  key={method.value}
                  name="paymentMethod"
                  checked={field.value === method.value}
                  onChange={() => field.onChange(method.value)}
                >
                  <RadioLabel>{method.label}</RadioLabel>
                </RadioButton>
              ))}
            </RadioGroup>
          )}
        />
      </FormGroup>

      <FormGroup label="통화 가능 시간" isHorizontal cssStyle={formGroupStyle}>
        <Controller
          name="contactAvailableTime"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Select value={field.value} onChange={field.onChange}>
              <option value="" disabled>
                통화 가능 시간을 선택해 주세요
              </option>
              {CALL_TIME_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          )}
        />
      </FormGroup>

      <FormGroup
        id="inquiryContent"
        label="문의 내용"
        isHorizontal
        errorText={errors?.inquiryContent?.message}
        cssStyle={formGroupStyle}
      >
        <Controller
          name="inquiryContent"
          control={control}
          rules={{ maxLength: { value: 1000, message: '최대 1000자까지 입력 가능합니다' } }}
          render={({ field }) => (
            <Input
              id="inquiryContent"
              name={field.name}
              multiline={5}
              placeholder="문의를 원하시는 내용에 대해 기입해 주세요"
              maxLength={1000}
              value={field.value}
              onChange={(value) => field.onChange(value)}
              cssStyle={inputStyle}
            />
          )}
        />
      </FormGroup>
    </FormCard>
  );
};

export default ContactForm;

const FormCard = styled.div`
  width: 100%;
  background: ${color.white};
  border-radius: 24px;
  padding: 40px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  ${onMobile} {
    padding: 24px;
  }
`;

const EmailFormGroupWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const HelpText = styled.p`
  margin: 0 0 0 110px;
  font-size: 14px;
  line-height: 20px;
  color: #555555;

  ${onMobile} {
    margin-left: 0;
  }
`;

const DateRange = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DateInput = styled(Flatpickr)`
  flex: 1;
  min-width: 0;
  width: 100%;
  height: 38px;
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 14px;
  line-height: 20px;
  font-family: 'Pretendard', sans-serif;
  color: ${color.text_default};
  cursor: pointer;

  &::placeholder {
    color: #adadad;
  }

  &:focus {
    outline: none;
    border-color: #7800ff;
  }
`;

const Select = styled.select<{ value?: string }>`
  flex: 1;
  width: 100%;
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 14px;
  line-height: 20px;
  font-family: 'Pretendard', sans-serif;
  color: ${({ value }) => (value ? color.text_default : '#adadad')};
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M4 6L8 10L12 6' stroke='%23555555' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 36px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #7800ff;
  }
`;

const DurationWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const DurationInput = styled.input`
  width: 80px;
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 14px;
  line-height: 20px;
  font-family: 'Pretendard', sans-serif;
  color: ${color.text_default};

  &::placeholder {
    color: #adadad;
  }

  &:focus {
    outline: none;
    border-color: #7800ff;
  }
`;

const DurationUnit = styled.span`
  padding: 8px 0;
  font-size: 14px;
  line-height: 20px;
  color: ${color.text_default};
`;

const RadioGroup = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 0;
`;

const RadioLabel = styled.span`
  font-size: 14px;
  line-height: 20px;
`;
